import { zodResolver } from '@hookform/resolvers/zod'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import {
  Backdrop,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldHint,
  FieldLabel,
  Input,
  OverlayWrapper,
} from '@firstbite/ui'
import { SavedView } from 'models/saved_view'

const savedFilterFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(50, 'Must be 50 characters or less'),
  shared: z.boolean(),
})

export type SavedFilterFormData = z.infer<typeof savedFilterFormSchema>

export interface SavedFilterModalProps {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: SavedFilterFormData) => Promise<unknown>
  savedFilterToEdit?: SavedView | null
  confirmButtonText?: string
  loading?: boolean
  title?: string
  description?: string
}

export function SavedFilterModal({
  show,
  handleClose,
  onSubmitSuccess,
  submitApiAction,
  savedFilterToEdit,
  confirmButtonText = 'Save',
  loading = false,
  title = 'Save Filter',
  description = 'Create a new filter to save this view, which includes both filters and any search terms.',
}: SavedFilterModalProps) {
  const values = useMemo<SavedFilterFormData>(
    () => ({
      name: savedFilterToEdit?.name ?? '',
      shared: savedFilterToEdit?.shared ?? true,
    }),
    [savedFilterToEdit]
  )

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    setError,
    reset,
    watch,
    setValue,
  } = useForm<SavedFilterFormData>({
    resolver: zodResolver(savedFilterFormSchema),
    // `values` (not `defaultValues`) keeps the form in sync when
    // savedFilterToEdit changes — replaces the old reset-in-useEffect.
    values,
    // Validate on blur (+ re-validate on blur) so the error only appears after
    // you leave the field, not on every keystroke — the DS2026-MODAL convention.
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })

  const _handleClose = () => {
    // Reset to `values` (the source data), NOT bare reset() — with `values`
    // (and no `defaultValues`) a bare reset() blanks the form, and since the
    // parent keeps this mounted across open/close, the blanked state is
    // deep-equal to what RHF last saw so it never re-syncs → empty on reopen.
    reset(values)
    handleClose()
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitApiAction(data)
      onSubmitSuccess?.()
      _handleClose()
    } catch (e: any) {
      if (e.response?.data?.error) {
        const errorMsg = e.response.data.error
        setError('name', { message: errorMsg })
      } else if (e.response?.data?.name) {
        setError('name', { message: e.response.data.name[0] })
      } else {
        setError('name', { message: `Error saving filter: ${e.message}` })
      }
    }
  })

  const isLoading = isSubmitting || loading
  const sharedChecked = watch('shared')

  return (
    <OverlayWrapper
      isOpen={show}
      onOpenChange={(open) => {
        if (!open) _handleClose()
      }}
    >
      <Backdrop>
        <Dialog>
          <form onSubmit={onSubmit}>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              {description && (
                <DialogDescription>{description}</DialogDescription>
              )}
            </DialogHeader>

            <DialogBody className="space-y-4">
              <Field status={errors.name ? 'error' : 'default'}>
                <FieldLabel htmlFor="saved-filter-name">Filter name</FieldLabel>
                <Input
                  {...register('name')}
                  id="saved-filter-name"
                  placeholder="Hint: make names descriptive to help find them later!"
                  disabled={isLoading}
                />
                {errors.name && <FieldHint>{errors.name.message}</FieldHint>}
              </Field>

              <Checkbox
                id="saved-filter-shared"
                checked={sharedChecked}
                onChange={(e) => setValue('shared', e.target.checked)}
                disabled={isLoading}
                label="Share filter with team"
              />
            </DialogBody>

            <DialogFooter>
              <DialogClose appearance="outline" disabled={isLoading}>
                Cancel
              </DialogClose>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                disabled={!isValid}
              >
                {confirmButtonText}
              </Button>
            </DialogFooter>
          </form>
        </Dialog>
      </Backdrop>
    </OverlayWrapper>
  )
}
